import { ReactNode, createContext, useCallback, useEffect, useMemo } from 'react';
import { ReactFacebookFailureResponse, ReactFacebookLoginInfo } from 'react-facebook-login';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { AuthData, SignUpData, SignUpGoogleResponseData } from '@packages/shared/core/auth/entities';
import { useFacebookSignUp, useGoogleSignUp } from '@packages/shared/core/auth/hooks';
import { useRouter, useServices } from '@packages/shared/core/common/hooks';
import { delay } from '@packages/shared/core/common/utils/delay';
import { FlowName, QuizData, QuizStepData, SplitStepData, StepTypes } from '@packages/shared/core/quiz/entities';
import { updateUserPreferences as updatePreferences } from '@packages/shared/core/user/store/actions';
import { updateFieldInteraction } from '@packages/shared/core/user/store/userInteraction/actions';
import { getUserPreferences } from '@packages/shared/core/user/store/selectors';
import { UserState } from '@packages/shared/core/user/store/user/state';
import { signIn, signUpViaQuiz } from '@packages/shared/core/user/store/user/thunks';
import { useFeatureFlags } from '@packages/shared/core/feature-flags/hooks';
import { joinUrls } from '@packages/shared/core/common/utils/url';

export type QuizContextData = {
  getStepData: (id: string) => QuizStepData;
  userPreferences: UserState;
  updateUserPreferences: (preference: keyof UserState, preferenceData: unknown) => void;
  updateUserInteraction: (field: string) => void;
  trackAnalyticForStep: (id: string) => void;
  updateUserSettings: (emailConsent: boolean) => void;
  signInUser: (credentials: AuthData) => Promise<void>;
  signUpUser: (data: SignUpData) => Promise<void>;
  signUpWithGoogle: (data: SignUpGoogleResponseData) => Promise<void>;
  signUpWithFacebook: (data: ReactFacebookLoginInfo | ReactFacebookFailureResponse) => Promise<void>;
  navigateToInitialStep: () => void;
  next: (args: NextArgs) => Promise<void>;
  back: (id: string) => Promise<void>;
  baseUrl: string;
  totalSteps: number;
  flowName: FlowName;
};

export const QuizContext = createContext<QuizContextData>(undefined!);

type QuizProviderProps = {
  children?: ReactNode;
  data: QuizData;
};

export interface NextArgs {
  id: string;
  text?: string;
  key?: string;
}

export const QuizProvider = ({ data, children }: QuizProviderProps) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { signUpGoogle } = useGoogleSignUp(data.name);
  const { signUpFacebook } = useFacebookSignUp(data.name);
  const { analyticsService, userService } = useServices();
  const { featureFlags } = useFeatureFlags();

  const preferences = useSelector(getUserPreferences);

  const store = useStore();
  const rootState = store.getState();

  const { baseUrl, steps, nextStage, previousStage, initialId, lastId, name } = data;

  const getStepData = (id: string) => {
    const step = steps[id];

    if (step && 'getStepData' in step) {
      return step.getStepData(rootState, featureFlags);
    }

    return step;
  };

  const totalSteps = useMemo(
    () => getStepData(lastId).index,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastId, steps],
  );

  const getNextStep = (id: string, choice?: string): string => {
    const { next } = getStepData(id);
    let nextStepId = next;

    const userPreferences = getUserPreferences(store.getState());

    if (!next) return '';
    if (getStepData(next).screenType === StepTypes.SPLIT) {
      const splitStep = steps[next] as SplitStepData;
      const choosenStep = choice || splitStep.getStep(userPreferences, featureFlags);
      nextStepId = splitStep.next || choosenStep;
    }

    return nextStepId;
  };

  const getStepForMoveBack = (id: string) => {
    const { prev } = getStepData(id);
    if (prev && getStepData(prev).screenType === StepTypes.SPLIT) {
      return getStepData(prev).prev;
    }

    return prev;
  };

  const updateUserPreferences = async (preference: keyof UserState, preferenceData: unknown) => {
    dispatch(updatePreferences(preference, preferenceData));
  };

  const updateUserInteraction = useCallback(
    (field: string) => {
      dispatch(updateFieldInteraction(field));
    },
    [dispatch],
  );

  const updateUserSettings = async (emailConsent: boolean) => {
    await userService.setEmailConsent(emailConsent);
    await analyticsService.setEmailConsent(emailConsent);
  };

  const trackAnalyticForStep = (id: string) => {
    const step = getStepData(id);

    if (!step || !step.analytic || !step.analytic?.baseEvent) return;

    analyticsService.trackQuizEvent(step.analytic.baseEvent, step.analytic.baseEventProperties || {});
  };

  const trackAnalyticUserChoice = (id: string, choice: string) => {
    const step = getStepData(id);
    if (step.analytic?.clickEvent) {
      analyticsService.trackQuizEvent(step.analytic.clickEvent, {
        context: choice,
      });
    }
  };

  const next = async ({ id, text, key }: NextArgs) => {
    await delay(500);

    const nextStep = getNextStep(id, key);
    const step = getStepData(id);

    if (text) {
      trackAnalyticUserChoice(id, text);
    } else if (step.analytic?.clickEvent) {
      analyticsService.trackQuizEvent(step.analytic.clickEvent, {});
    }

    if (!nextStep) {
      router.push(joinUrls(baseUrl, nextStage));
      return;
    }

    router.push(joinUrls(baseUrl, nextStep));
  };

  const back = async (id: string) => {
    await delay(300);
    if (id === initialId) {
      router.push(previousStage);
      return;
    }

    const prevStep = getStepForMoveBack(id);

    if (!prevStep) return;

    router.push(joinUrls(baseUrl, prevStep));
  };

  const signInUser = async (credentials: AuthData) => {
    await dispatch(signIn(credentials));
  };

  const signUpUser = async (signUpData: SignUpData) => {
    await dispatch(
      signUpViaQuiz({
        ...signUpData,
        quizName: data.name,
      }),
    );
  };

  const signUpWithGoogle = async (userData: SignUpGoogleResponseData) => {
    await signUpGoogle(userData);
  };

  const signUpWithFacebook = async (response: ReactFacebookLoginInfo | ReactFacebookFailureResponse) => {
    await signUpFacebook(response);
  };

  const navigateToInitialStep = () => {
    router.push(joinUrls(baseUrl, steps[0].id));
  };

  useEffect(() => {
    analyticsService.setCurrentQuizName(data.name);
  }, [analyticsService, data]);

  return (
    <QuizContext.Provider
      value={{
        signInUser,
        signUpUser,
        signUpWithGoogle,
        signUpWithFacebook,
        trackAnalyticForStep,
        userPreferences: preferences,
        updateUserPreferences,
        updateUserInteraction,
        updateUserSettings,
        getStepData,
        navigateToInitialStep,
        next,
        back,
        baseUrl,
        totalSteps,
        flowName: name,
      }}
    >
      {children}
    </QuizContext.Provider>
  );
};
